import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { publicApi, currency } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import * as Icons from "lucide-react";
import { categoryMeta } from "@/lib/api";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/lib/content";
import { Smartphone, ArrowRight } from "lucide-react";

function CategoryCard({ cat }) {
  const meta = categoryMeta[cat.slug] || {};
  const Icon = Icons[meta.icon] || Icons.Sparkles;
  return (
    <Link to={`/catalog?category=${cat.slug}`} data-testid={`category-card-${cat.slug}`}
      className="gold-card group relative aspect-square flex flex-col justify-end p-5 overflow-hidden">
      <img src={cat.image} alt={cat.name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
      <div className="relative">
        <Icon className="w-6 h-6 text-gold mb-2" />
        <p className="font-serif text-cream text-lg leading-tight group-hover:text-gold transition-colors">{cat.name}</p>
      </div>
    </Link>
  );
}

export default function Landing() {
  const { user, login } = useAuth();
  const { t } = useContent();
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: async () => (await publicApi.get("/settings")).data });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await publicApi.get("/categories")).data });
  const { data: featured = [] } = useQuery({ queryKey: ["featured"], queryFn: async () => (await publicApi.get("/products", { params: { featured: true } })).data });
  const { data: faqs = [] } = useQuery({ queryKey: ["faqs"], queryFn: async () => (await publicApi.get("/faqs")).data });
  const spotlightId = settings.spotlight_product_id;
  const { data: spotlight } = useQuery({
    queryKey: ["spotlight", spotlightId],
    queryFn: async () => (await publicApi.get(`/products/${spotlightId}`)).data,
    enabled: !!spotlightId,
  });

  const handleSignIn = () => { localStorage.setItem("post_login_redirect", "/catalog"); login(); };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center">
        <img src={settings.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30 z-10" />
        <div className="absolute inset-0 grain z-10" />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl animate-fade-up">
            <p className="overline-label mb-5">{settings.tagline}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-white">
              {settings.hero_title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-cream/70 leading-relaxed max-w-xl">
              {settings.hero_subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/catalog" data-testid="hero-cta-browse" className="btn-gold">
                {t("landing.hero_cta_browse")} <ArrowRight className="w-4 h-4" />
              </Link>
              {!user && (
                <button onClick={handleSignIn} data-testid="hero-cta-signin" className="btn-silver">{t("landing.hero_cta_signin")}</button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SPOTLIGHT */}
      {spotlight && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div data-testid="spotlight-rental-card" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-ink-700 border border-gold/40 rounded-2xl p-6 lg:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
            <div className="lg:col-span-6">
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img src={spotlight.image_urls?.[0]} alt={spotlight.name} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-6">
              <p className="overline-label mb-3">{settings.spotlight_label || "Rental of the Month"}</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-cream leading-tight">{spotlight.name}</h2>
              <p className="mt-4 text-muted2 leading-relaxed">{spotlight.description}</p>
              <p className="mt-6 text-2xl text-gold-bright font-semibold">{currency(spotlight.price_per_day)}<span className="text-muted2 text-sm font-normal"> / day</span></p>
              <Link to={`/product/${spotlight.id}`} data-testid="spotlight-reserve-button" className="btn-gold mt-7">
                Reserve This <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline-label mb-2">{t("landing.categories_overline")}</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-cream">{t("landing.categories_title")}</h2>
          </div>
          <Link to="/catalog" className="hidden sm:inline text-gold hover:text-gold-bright text-sm">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {categories.map((c) => <CategoryCard key={c.slug} cat={c} />)}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-10">
            <p className="overline-label mb-2">{t("landing.featured_overline")}</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-cream">{t("landing.featured_title")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* FAQ PREVIEW */}
      {faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <p className="overline-label mb-2">{t("landing.faq_overline")}</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-cream">{t("landing.faq_title")}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.slice(0, 4).map((f) => (
              <AccordionItem key={f.id} value={f.id} className="gold-card !border-gold/20 px-5">
                <AccordionTrigger className="text-cream hover:text-gold text-left font-serif text-lg">{f.question}</AccordionTrigger>
                <AccordionContent className="text-muted2 leading-relaxed">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link to="/faqs" className="btn-silver">{t("landing.faq_cta")}</Link>
          </div>
        </section>
      )}

      {/* GET THE APP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gold-deep border-y border-gold/40 rounded-2xl px-8 py-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-lg">
            <Smartphone className="w-8 h-8 text-gold mb-4" />
            <h2 className="font-serif text-3xl text-cream">{t("landing.app_title")}</h2>
            <p className="mt-3 text-sterling">{t("landing.app_subtitle")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <button data-testid="get-app-ios" className="btn-silver">App Store</button>
            <button data-testid="get-app-android" className="btn-silver">Google Play</button>
          </div>
        </div>
      </section>
    </div>
  );
}
